import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { buildAuthHeaders, getAccessToken } from '../utils/authHeaders';

const apiUrl = process.env.REACT_APP_API_URL;

const createEmptySummary = () => ({
  clientId: '',
  financialYear: '',
  totalRevenue: 0,
  totalExpense: 0,
  totalAssets: 0,
  totalLiabilities: 0,
  grossTotalIncome: 0,
  businessIncomeAfterTaxDepreciation: 0,
  taxDepreciation: 0,
  shortTermCapitalGain: 0,
  taxableShortTermCapitalGain: 0,
  longTermCapitalGainGross: 0,
  longTermCapitalGainThresholdRelief: 0,
  taxableLongTermCapitalGain: 0,
  incomeEntryCount: 0,
  expenseEntryCount: 0,
  assetCount: 0,
  deliveryTradeCount: 0,
  deliveryBusinessCount: 0,
  deliveryInvestmentCount: 0,
});

export const fetchDashboardSummary = createAsyncThunk(
  'dashboard/fetchDashboardSummary',
  async (_, { getState, rejectWithValue }) => {
    const { clientId, financialYear } = getState().app;
    const accessToken = getAccessToken(getState().auth?.access_token);
    try {
      const response = await fetch(
        `${apiUrl}/api/dashboard/summary?clientId=${encodeURIComponent(clientId)}&financialYear=${encodeURIComponent(financialYear)}`,
        {
          headers: buildAuthHeaders(accessToken),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard summary');
      }
      return response.json();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch dashboard summary');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    summary: createEmptySummary(),
    loading: false,
    error: '',
  },
  reducers: {
    clearDashboardSummary(state) {
      state.summary = createEmptySummary();
      state.loading = false;
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.summary = { ...createEmptySummary(), ...action.payload };
        state.loading = false;
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch dashboard summary';
      });
  },
});

export const { clearDashboardSummary } = dashboardSlice.actions;
export default dashboardSlice.reducer;